// DRM Engine for handling FairPlay DRM
// Supports multiple keys per HLS file with proper session tracking
//
// Two FairPlay paths are supported:
// - Modern EME (`com.apple.fps` via requestMediaKeySystemAccess). Default on
//   iOS < 26 where it is stable.
// - Legacy WebKit-prefixed API (`com.apple.fps.1_0` via WebKitMediaKeys,
//   webkitneedkey/webkitkeymessage). Preferred on WebKit 26+ because iOS 26
//   shipped a regression where modern EME sessions on native HLS `src=`
//   playback intermittently never emit the license request `message` event
//   or reject generateRequest with NotSupportedError (see shaka-player#9794,
//   shaka-player#9491, muxinc/elements#1261).
// If the active path fails before any key becomes usable, the engine switches
// to the other path once and reloads the media element.

import { getWebKitMajorVersion } from './browser-detection.js';

const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

class Logger {
  constructor(level = LogLevel.INFO) {
    this.level = level;
  }

  debug(...args) {
    if (this.level <= LogLevel.DEBUG) console.debug('[DRM]', ...args);
  }

  info(...args) {
    if (this.level <= LogLevel.INFO) console.info('[DRM]', ...args);
  }

  warn(...args) {
    if (this.level <= LogLevel.WARN) console.warn('[DRM]', ...args);
  }

  error(...args) {
    if (this.level <= LogLevel.ERROR) console.error('[DRM]', ...args);
  }
}

const DRM_ERROR_MESSAGE =
  'There was an error with the content protection system.';
const DRM_ACCESS_ERROR_MESSAGE =
  "This video cannot be played on your device because it doesn't meet the content protection requirements.";
const WEBKIT_NEED_KEY_FALLBACK_DELAY_MS = 50;
const LEGACY_KEY_SYSTEM = 'com.apple.fps.1_0';
const MESSAGE_TIMEOUT_MS = 30000;
// When the other FairPlay path is still available as a fallback, give up on
// the license request message quickly. SPC generation is local to the device,
// so a healthy CDM responds well within this window.
const PATH_SWITCH_MESSAGE_TIMEOUT_MS = 5000;

function createDrmError(code, message, cause, options = {}) {
  const error = new Error(message);
  error.drmErrorCode = code;
  error.drmErrorMessage = message;
  error.drmFatal = options.fatal === true;
  error.cause = cause;
  return error;
}

/**
 * Extract key ID from FairPlay initData
 * @param {ArrayBuffer} initData
 * @returns {string} Hex string of key ID
 */
function extractKeyId(initData) {
  // FairPlay initData is typically the skd:// URI as UTF-16
  // Convert to string and use as identifier
  const uint8 = new Uint8Array(initData);
  // Create a hash/identifier from the initData bytes
  let hash = 0;
  for (let i = 0; i < uint8.length; i++) {
    hash = ((hash << 5) - hash + uint8[i]) | 0;
  }
  return hash.toString(16);
}

/**
 * Encode a string as UTF-16LE bytes (the encoding WebKit expects for the
 * content ID segment of legacy FairPlay initData).
 * @param {string} value
 * @returns {Uint8Array}
 */
function stringToUtf16LeBytes(value) {
  const bytes = new Uint8Array(value.length * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < value.length; i++) {
    view.setUint16(i * 2, value.charCodeAt(i), true);
  }
  return bytes;
}

/**
 * Extract the FairPlay content ID from webkitneedkey initData.
 * The initData carries the `skd://` key URI (usually UTF-16LE, sometimes with
 * a length prefix); the content ID is everything after the scheme.
 * @param {ArrayBuffer} initData
 * @returns {string|null}
 */
function extractLegacyContentId(initData) {
  const bytes = new Uint8Array(initData);
  for (const encoding of ['utf-16le', 'utf-8']) {
    try {
      const text = new TextDecoder(encoding).decode(bytes);
      const start = text.indexOf('skd://');
      if (start >= 0) {
        const contentId = text
          .slice(start + 'skd://'.length)
          .split(String.fromCharCode(0))
          .join('')
          .trim();
        if (contentId) return contentId;
      }
    } catch (_error) {
      // Try the next encoding.
    }
  }
  return null;
}

/**
 * Build the concatenated initData buffer required by the legacy WebKit
 * FairPlay API: [initData][u32le contentId length][contentId UTF-16LE]
 * [u32le certificate length][certificate].
 * @param {ArrayBuffer} initData
 * @param {string} contentId
 * @param {ArrayBuffer} certificate
 * @returns {Uint8Array}
 */
function buildLegacyFairPlayInitData(initData, contentId, certificate) {
  const initDataBytes = new Uint8Array(initData);
  const contentIdBytes = stringToUtf16LeBytes(contentId);
  const certificateBytes = new Uint8Array(certificate);

  const result = new Uint8Array(
    initDataBytes.byteLength + 4 + contentIdBytes.byteLength +
      4 + certificateBytes.byteLength,
  );
  const view = new DataView(result.buffer);
  let offset = 0;

  result.set(initDataBytes, offset);
  offset += initDataBytes.byteLength;

  view.setUint32(offset, contentIdBytes.byteLength, true);
  offset += 4;
  result.set(contentIdBytes, offset);
  offset += contentIdBytes.byteLength;

  view.setUint32(offset, certificateBytes.byteLength, true);
  offset += 4;
  result.set(certificateBytes, offset);

  return result;
}

export class StreamDrmEngine {
  constructor(fpLicenseUrl, fpCertificateUrl, logLevel = LogLevel.INFO) {
    this.fpLicenseUrl = fpLicenseUrl;
    this.fpCertificateUrl = fpCertificateUrl;
    this.logger_ = new Logger(logLevel);
    this.fpCertificate = null;

    // MediaKeys setup tracking
    this.preparedMediaKeysPromise = null;
    this.mediaKeysPromise = null;
    this.mediaKeysReady = false;

    // Session tracking: Map<keyId, MediaKeySession>
    this.sessions = new Map();
    // Pending requests: Map<keyId, Promise<void>> - prevents duplicate processing
    this.pendingRequests = new Map();
    // Track which keys have become usable
    this.usableKeys = new Set();
    // Timeout for keys that never become usable
    this._keyUsableTimeout = null;
    // Timeout for per-key failures that may be recovered by another key event.
    this._processingFailureTimeout = null;
    this._pendingProcessingFailure = null;
    this._hasStandardEncryptedEvent = false;
    this._queuedWebkitNeedKeyEvents = new Set();

    // FairPlay path selection. When true, the legacy WebKit-prefixed API
    // (WebKitMediaKeys) is used instead of modern EME.
    this.useLegacyWebKit = false;
    // Only one automatic switch between paths is attempted per initialization.
    this._pathSwitchAttempted = false;
    // In-flight certificate fetch, deduped across callers.
    this._certificatePromise = null;

    // Bound event handler for cleanup
    this._boundOnEncrypted = null;
    this._boundOnWebkitNeedKey = null;
    this._videoElement = null;

    // Error callback for dispatching errors to the player
    this.onError = null;
  }

  /**
   * Load FairPlay certificate from server.
   * Concurrent callers share a single fetch; failed fetches can be retried.
   */
  async loadFpCertificate() {
    if (this.fpCertificate) {
      return;
    }
    if (!this._certificatePromise) {
      this._certificatePromise = this._fetchFpCertificate();
    }
    try {
      await this._certificatePromise;
    } catch (e) {
      this._certificatePromise = null;
      throw e;
    }
  }

  /**
   * Fetch the FairPlay certificate from the server.
   */
  async _fetchFpCertificate() {
    try {
      this.logger_.debug(
        'Loading FairPlay certificate from:',
        this.fpCertificateUrl,
      );
      const response = await fetch(this.fpCertificateUrl);
      if (!response.ok) {
        throw new Error(`Certificate fetch failed: ${response.status}`);
      }
      this.fpCertificate = await response.arrayBuffer();
      this.logger_.debug(
        'FairPlay certificate loaded, size:',
        this.fpCertificate.byteLength,
      );
    } catch (e) {
      this.logger_.error(
        `Could not load FairPlay certificate at ${this.fpCertificateUrl}`,
        e,
      );
      throw createDrmError(
        'keySystemServerCertificateRequestFailed',
        DRM_ERROR_MESSAGE,
        e,
        { fatal: true },
      );
    }
  }

  /**
   * Create and configure MediaKeys for the video element
   * Uses a promise to ensure only one setup happens even with concurrent calls
   * @param {HTMLVideoElement} video
   * @returns {Promise<MediaKeys>}
   */
  async setupMediaKeys(video) {
    // Return immediately if already attached to this element.
    if (this.mediaKeysReady && video.mediaKeys) {
      return video.mediaKeys;
    }

    // Return existing attach promise only while setup is in progress. Once it
    // has resolved, a new video element still needs MediaKeys attached.
    if (this.mediaKeysPromise && !this.mediaKeysReady) {
      return this.mediaKeysPromise;
    }

    this.mediaKeysPromise = (async () => {
      const mediaKeys = await this.prepareMediaKeys();

      if (!video.mediaKeys) {
        this.logger_.debug('Setting MediaKeys on video element');
        try {
          await video.setMediaKeys(mediaKeys);
        } catch (e) {
          throw createDrmError('keySystemNoKeys', DRM_ERROR_MESSAGE, e, {
            fatal: true,
          });
        }
      }

      this.mediaKeysReady = true;
      return mediaKeys;
    })();

    try {
      return await this.mediaKeysPromise;
    } catch (e) {
      // Reset on failure to allow retry.
      this.mediaKeysPromise = null;
      this.mediaKeysReady = false;
      throw e;
    }
  }

  /**
   * Prepare MediaKeys and install the FairPlay server certificate.
   * @returns {Promise<MediaKeys>}
   */
  async prepareMediaKeys() {
    if (this.preparedMediaKeysPromise) {
      return this.preparedMediaKeysPromise;
    }

    if (!this.fpCertificate) {
      await this.loadFpCertificate();
    }

    this.preparedMediaKeysPromise = this._createMediaKeys();

    try {
      return await this.preparedMediaKeysPromise;
    } catch (e) {
      this.preparedMediaKeysPromise = null;
      throw e;
    }
  }

  /**
   * Internal method to create MediaKeys
   * @returns {Promise<MediaKeys>}
   */
  async _createMediaKeys() {
    this.logger_.debug('Requesting MediaKeySystemAccess for com.apple.fps');

    let access;
    try {
      access = await navigator.requestMediaKeySystemAccess(
        'com.apple.fps',
        [
          {
            initDataTypes: ['cenc', 'sinf', 'skd'],
            videoCapabilities: [
              { contentType: 'application/vnd.apple.mpegurl', robustness: '' },
            ],
            distinctiveIdentifier: 'not-allowed',
            persistentState: 'not-allowed',
            sessionTypes: ['temporary'],
          },
        ],
      );
    } catch (e) {
      throw createDrmError(
        'keySystemNoAccess',
        DRM_ACCESS_ERROR_MESSAGE,
        e,
        { fatal: true },
      );
    }

    this.logger_.debug('Creating MediaKeys');
    let mediaKeys;
    try {
      mediaKeys = await access.createMediaKeys();
    } catch (e) {
      throw createDrmError('keySystemNoKeys', DRM_ERROR_MESSAGE, e, {
        fatal: true,
      });
    }

    this.logger_.debug('Setting server certificate');
    let certificateResult;
    try {
      certificateResult = await mediaKeys.setServerCertificate(
        this.fpCertificate,
      );
    } catch (e) {
      throw createDrmError(
        'keySystemServerCertificateUpdateFailed',
        DRM_ERROR_MESSAGE,
        e,
        { fatal: true },
      );
    }
    this.logger_.debug(
      'FairPlay server certificate result:',
      certificateResult,
    );

    this.logger_.debug('FairPlay MediaKeys prepared');
    return mediaKeys;
  }

  /**
   * Initialize FairPlay DRM by setting up the encrypted event listeners.
   * MediaKeys and license work starts from the key events so native HLS can
   * get a source immediately and WebKit owns the load sequence. On the legacy
   * WebKit path, WebKitMediaKeys is attached synchronously here so keys are in
   * place before the caller assigns the native HLS source.
   * @param {HTMLVideoElement} videoEl
   */
  initFairPlay(videoEl) {
    try {
      // Clean up any previous initialization
      this.cleanup();

      // Store reference for cleanup
      this._videoElement = videoEl;

      this.useLegacyWebKit = this._shouldPreferLegacyWebKit(videoEl);
      if (this.useLegacyWebKit) {
        try {
          this._ensureLegacyMediaKeys(videoEl);
        } catch (e) {
          this.logger_.warn(
            'Legacy WebKit FairPlay setup failed, using modern EME:',
            e,
          );
          this.useLegacyWebKit = false;
        }
      }

      this._boundOnEncrypted = (e) => this.onFpEncrypted(e);
      this._boundOnWebkitNeedKey = (e) => this.onFpEncrypted(e);

      this.logger_.debug('Adding encrypted event listener');
      videoEl.addEventListener('encrypted', this._boundOnEncrypted);
      videoEl.addEventListener('webkitneedkey', this._boundOnWebkitNeedKey);

      // Prefetch the certificate so the first key event does not wait on the
      // network. Failures are surfaced when a key event needs the certificate.
      this.loadFpCertificate().catch(() => {});

      this.logger_.info(
        `FairPlay DRM initialized (${
          this.useLegacyWebKit ? 'legacy WebKit' : 'modern EME'
        } path)`,
      );
    } catch (e) {
      this.logger_.error('Failed to initialize FairPlay DRM:', e);
      throw e;
    }
  }

  /**
   * Whether the legacy WebKit-prefixed FairPlay API is available.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  _isLegacyWebKitSupported(video) {
    const WebKitMediaKeysCtor = globalThis.WebKitMediaKeys;
    if (
      !WebKitMediaKeysCtor ||
      typeof video?.webkitSetMediaKeys !== 'function'
    ) {
      return false;
    }
    try {
      if (typeof WebKitMediaKeysCtor.isTypeSupported === 'function') {
        return WebKitMediaKeysCtor.isTypeSupported(
          LEGACY_KEY_SYSTEM,
          'video/mp4',
        );
      }
    } catch (_error) {
      return false;
    }
    return true;
  }

  /**
   * Prefer the legacy WebKit path on WebKit 26+ where the modern EME path on
   * native HLS playback is unreliable. Older versions keep modern EME.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  _shouldPreferLegacyWebKit(video) {
    if (!this._isLegacyWebKitSupported(video)) {
      return false;
    }
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.requestMediaKeySystemAccess !== 'function'
    ) {
      return true;
    }
    const version = getWebKitMajorVersion();
    return version !== null && version >= 26;
  }

  /**
   * Attach WebKitMediaKeys to the video element for the legacy path.
   * @param {HTMLVideoElement} video
   */
  _ensureLegacyMediaKeys(video) {
    if (video.webkitKeys) {
      return;
    }

    const WebKitMediaKeysCtor = globalThis.WebKitMediaKeys;
    if (!WebKitMediaKeysCtor) {
      throw createDrmError(
        'keySystemNoAccess',
        DRM_ACCESS_ERROR_MESSAGE,
        new Error('WebKitMediaKeys is not available'),
        { fatal: true },
      );
    }

    try {
      video.webkitSetMediaKeys(new WebKitMediaKeysCtor(LEGACY_KEY_SYSTEM));
    } catch (error) {
      throw createDrmError(
        'keySystemNoAccess',
        DRM_ACCESS_ERROR_MESSAGE,
        error,
        { fatal: true },
      );
    }

    if (!video.webkitKeys) {
      throw createDrmError(
        'keySystemNoAccess',
        DRM_ACCESS_ERROR_MESSAGE,
        new Error('webkitSetMediaKeys did not attach media keys'),
        { fatal: true },
      );
    }

    this.logger_.debug('Legacy WebKitMediaKeys attached to video element');
  }

  /**
   * Handle encrypted event from video element
   * Implements proper session tracking to handle multiple keys
   * @param {MediaEncryptedEvent} event
   */
  async onFpEncrypted(event) {
    const initDataType = event.initDataType || 'skd';
    const initData = event.initData;
    const eventType = event.type || 'encrypted';

    // Skip invalid events
    if (!initData) {
      this.logger_.warn(
        'Skipping invalid encrypted event - missing initData',
      );
      return;
    }

    const keyId = extractKeyId(initData);

    this.logger_.debug(`${eventType} event received for key:`, keyId);

    // Legacy WebKit path: only webkitneedkey events matter and they are
    // processed immediately (WebKitMediaKeys is already attached).
    if (this.useLegacyWebKit) {
      if (eventType !== 'webkitneedkey') {
        return;
      }
      return await this._processFairPlayKeyEvent(
        event.target,
        initDataType,
        initData,
        keyId,
      );
    }

    if (eventType === 'encrypted') {
      this._hasStandardEncryptedEvent = true;
      this._cancelQueuedWebkitNeedKeyEvents();
    }

    if (eventType === 'webkitneedkey') {
      if (this._hasStandardEncryptedEvent) {
        return;
      }

      return await this._queueWebkitNeedKeyEvent(
        event.target,
        initDataType,
        initData,
        keyId,
      );
    }

    await this._processFairPlayKeyEvent(
      event.target,
      initDataType,
      initData,
      keyId,
    );
  }

  async _processFairPlayKeyEvent(
    video,
    initDataType,
    initData,
    keyId,
  ) {
    // Check if we already have a session for this key
    if (this.sessions.has(keyId)) {
      this.logger_.debug(`Session already exists for key ${keyId}, skipping`);
      return;
    }

    // Check if we're already processing this key (race condition prevention)
    if (this.pendingRequests.has(keyId)) {
      this.logger_.debug(
        `Request already pending for key ${keyId}, skipping duplicate`,
      );
      return;
    }

    // Process keys in parallel to avoid blocking when multiple keys are needed
    const processingPromise = this._processEncryptedEvent(
      video,
      initDataType,
      initData,
      keyId,
    );
    this.pendingRequests.set(keyId, processingPromise);

    try {
      await processingPromise;
    } catch (error) {
      this.logger_.error(`Processing failed for key ${keyId}:`, error);
      this._dispatchErrorForFailure(error);
    } finally {
      this.pendingRequests.delete(keyId);
    }
  }

  _queueWebkitNeedKeyEvent(video, initDataType, initData, keyId) {
    return new Promise((resolve) => {
      const queuedEvent = { timer: null, resolve };
      const timer = setTimeout(async () => {
        this._queuedWebkitNeedKeyEvents.delete(queuedEvent);

        if (this._hasStandardEncryptedEvent) {
          resolve();
          return;
        }

        await this._processFairPlayKeyEvent(
          video,
          initDataType,
          initData,
          keyId,
        );
        resolve();
      }, WEBKIT_NEED_KEY_FALLBACK_DELAY_MS);

      queuedEvent.timer = timer;
      this._queuedWebkitNeedKeyEvents.add(queuedEvent);
    });
  }

  _cancelQueuedWebkitNeedKeyEvents() {
    if (this._queuedWebkitNeedKeyEvents.size === 0) {
      return;
    }

    for (const queuedEvent of this._queuedWebkitNeedKeyEvents) {
      clearTimeout(queuedEvent.timer);
      queuedEvent.resolve();
    }

    this._queuedWebkitNeedKeyEvents.clear();
  }

  /**
   * Process an encrypted event and create a session on the active path.
   * On failure, attempts a one-time switch to the other FairPlay path.
   * @param {HTMLVideoElement} video
   * @param {string} initDataType
   * @param {ArrayBuffer} initData
   * @param {string} keyId
   */
  async _processEncryptedEvent(video, initDataType, initData, keyId) {
    try {
      if (this.useLegacyWebKit) {
        await this._processLegacyEncryptedEvent(video, initData, keyId);
      } else {
        await this._processModernEncryptedEvent(
          video,
          initDataType,
          initData,
          keyId,
        );
      }
    } catch (error) {
      this.logger_.error(
        `FairPlay processing failed for key ${keyId}:`,
        error,
      );
      this.sessions.delete(keyId);
      if (await this._maybeSwitchDrmPath(video, error)) {
        return;
      }
      throw error;
    }
  }

  /**
   * Modern EME (com.apple.fps) processing for one key.
   * @param {HTMLVideoElement} video
   * @param {string} initDataType
   * @param {ArrayBuffer} initData
   * @param {string} keyId
   */
  async _processModernEncryptedEvent(video, initDataType, initData, keyId) {
    // Ensure MediaKeys are set up
    await this.setupMediaKeys(video);

    // Create session
    this.logger_.debug(`Creating media key session for key ${keyId}`);
    let session;
    try {
      session = video.mediaKeys.createSession();
    } catch (error) {
      throw createDrmError('keySystemNoSession', DRM_ERROR_MESSAGE, error);
    }

    // Store session immediately to prevent duplicates
    this.sessions.set(keyId, session);

    // Listen for session events (for monitoring, not blocking)
    session.addEventListener('keystatuseschange', () => {
      this._onKeyStatusChange(session, keyId);
    });

    // Listen for the license request before generateRequest(). Safari can
    // dispatch the message immediately, and registering after the request races.
    let messageTimeout = null;
    const messagePromise = new Promise((resolve, reject) => {
      messageTimeout = setTimeout(() => {
        reject(
          createDrmError(
            'keySystemLicenseRequestFailed',
            DRM_ERROR_MESSAGE,
            new Error('Timeout waiting for license request message'),
          ),
        );
      }, this._getMessageTimeoutMs(video));

      session.addEventListener(
        'message',
        (e) => {
          clearTimeout(messageTimeout);
          resolve(e);
        },
        { once: true },
      );
    });

    // Generate license request
    this.logger_.debug(
      `Generating request for key ${keyId} with initDataType:`,
      initDataType,
    );

    try {
      const generateRequestPromise = Promise.resolve(
        session.generateRequest(initDataType, initData),
      );
      generateRequestPromise.catch(() => {});

      await Promise.race([
        generateRequestPromise,
        messagePromise.then(() => undefined),
      ]);
    } catch (error) {
      clearTimeout(messageTimeout);
      if (error?.drmErrorCode) {
        throw error;
      }
      throw createDrmError('keySystemNoSession', DRM_ERROR_MESSAGE, error);
    }

    // Wait for license request message
    const message = await messagePromise;

    this.logger_.debug(`Fetching license for key ${keyId}`);

    // Fetch license from server
    const response = await this.getResponse(message, this.fpLicenseUrl);

    this.logger_.debug(`License received for key ${keyId}, updating session`);

    // Update session with license
    try {
      await session.update(response);
    } catch (error) {
      throw createDrmError(
        'keySystemSessionUpdateFailed',
        DRM_ERROR_MESSAGE,
        error,
      );
    }

    // Don't wait for key to become usable - let playback proceed naturally
    // The browser will use the key when ready
    this.logger_.info(`FairPlay license applied for key ${keyId}`);

    // Start a timeout to detect if keys never become usable
    // This can happen due to HDCP/output restrictions on some platforms
    this._startKeyUsableTimeout();
  }

  /**
   * Legacy WebKit (com.apple.fps.1_0 / WebKitMediaKeys) processing for one key.
   * @param {HTMLVideoElement} video
   * @param {ArrayBuffer} initData
   * @param {string} keyId
   */
  async _processLegacyEncryptedEvent(video, initData, keyId) {
    await this.loadFpCertificate();
    this._ensureLegacyMediaKeys(video);

    const contentId = extractLegacyContentId(initData);
    if (!contentId) {
      throw createDrmError(
        'keySystemNoSession',
        DRM_ERROR_MESSAGE,
        new Error('Could not extract FairPlay content ID from initData'),
      );
    }

    const sessionInitData = buildLegacyFairPlayInitData(
      initData,
      contentId,
      this.fpCertificate,
    );

    this.logger_.debug(`Creating legacy media key session for key ${keyId}`);
    let session;
    try {
      session = video.webkitKeys.createSession('video/mp4', sessionInitData);
    } catch (error) {
      throw createDrmError('keySystemNoSession', DRM_ERROR_MESSAGE, error);
    }
    if (!session) {
      throw createDrmError(
        'keySystemNoSession',
        DRM_ERROR_MESSAGE,
        new Error('webkitKeys.createSession returned no session'),
      );
    }

    // Store session immediately to prevent duplicates
    this.sessions.set(keyId, session);

    session.addEventListener('webkitkeyadded', () => {
      this.logger_.info(`Key ${keyId} is now usable (legacy WebKit)`);
      this.usableKeys.add(keyId);
      this._clearProcessingFailureTimeout();
      if (this._keyUsableTimeout) {
        clearTimeout(this._keyUsableTimeout);
        this._keyUsableTimeout = null;
      }
    });

    session.addEventListener('webkitkeyerror', () => {
      const sessionError = session.error;
      this.logger_.error(
        `Legacy WebKit key error for key ${keyId}:`,
        sessionError?.code,
        sessionError?.systemCode,
      );
      this._dispatchErrorForFailure(
        createDrmError(
          'keySystemStatusInternalError',
          DRM_ERROR_MESSAGE,
          sessionError,
        ),
      );
    });

    // Wait for the SPC message from the CDM
    const message = await new Promise((resolve, reject) => {
      const messageTimeout = setTimeout(() => {
        reject(
          createDrmError(
            'keySystemLicenseRequestFailed',
            DRM_ERROR_MESSAGE,
            new Error('Timeout waiting for webkitkeymessage'),
          ),
        );
      }, this._getMessageTimeoutMs(video));

      session.addEventListener(
        'webkitkeymessage',
        (e) => {
          clearTimeout(messageTimeout);
          resolve(e);
        },
        { once: true },
      );
    });

    this.logger_.debug(`Fetching license for key ${keyId} (legacy WebKit)`);
    const response = await this.getResponse(message, this.fpLicenseUrl);

    try {
      session.update(new Uint8Array(response));
    } catch (error) {
      throw createDrmError(
        'keySystemSessionUpdateFailed',
        DRM_ERROR_MESSAGE,
        error,
      );
    }

    this.logger_.info(
      `FairPlay license applied for key ${keyId} (legacy WebKit)`,
    );
    this._startKeyUsableTimeout();
  }

  /**
   * License request message timeout. Shorter when the other FairPlay path is
   * still available so a stalled CDM triggers the path switch quickly.
   * @param {HTMLVideoElement} video
   * @returns {number}
   */
  _getMessageTimeoutMs(video) {
    return this._canSwitchDrmPath(video)
      ? PATH_SWITCH_MESSAGE_TIMEOUT_MS
      : MESSAGE_TIMEOUT_MS;
  }

  /**
   * Whether an automatic switch to the other FairPlay path is possible.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  _canSwitchDrmPath(video) {
    if (this._pathSwitchAttempted) return false;
    if (this.usableKeys.size > 0) return false;
    if (this.useLegacyWebKit) {
      return typeof navigator !== 'undefined' &&
        typeof navigator.requestMediaKeySystemAccess === 'function';
    }
    return this._isLegacyWebKitSupported(video);
  }

  /**
   * Switch to the other FairPlay path after a processing failure and reload
   * the media element so key events fire again.
   * @param {HTMLVideoElement} video
   * @param {unknown} error - The failure that triggered the switch attempt
   * @returns {Promise<boolean>} True if the switch was performed
   */
  async _maybeSwitchDrmPath(video, error) {
    // A certificate fetch failure would fail on both paths.
    if (error?.drmErrorCode === 'keySystemServerCertificateRequestFailed') {
      return false;
    }
    if (!this._canSwitchDrmPath(video)) {
      return false;
    }

    this._pathSwitchAttempted = true;
    const targetLegacy = !this.useLegacyWebKit;
    this.logger_.warn(
      `Switching FairPlay path to ${
        targetLegacy ? 'legacy WebKit' : 'modern EME'
      } after failure:`,
      error,
    );

    await this._resetDrmPathState(video);
    this.useLegacyWebKit = targetLegacy;

    if (targetLegacy) {
      try {
        this._ensureLegacyMediaKeys(video);
      } catch (e) {
        this.logger_.error('Legacy WebKit fallback setup failed:', e);
        this.useLegacyWebKit = false;
        return false;
      }
    }

    this._restartPlayback(video);
    return true;
  }

  /**
   * Tear down per-path DRM state before switching paths.
   * @param {HTMLVideoElement} video
   */
  async _resetDrmPathState(video) {
    this._closeAllSessions();
    this.pendingRequests.clear();
    this.usableKeys.clear();
    this._hasStandardEncryptedEvent = false;
    this._cancelQueuedWebkitNeedKeyEvents();
    this._clearProcessingFailureTimeout();
    if (this._keyUsableTimeout) {
      clearTimeout(this._keyUsableTimeout);
      this._keyUsableTimeout = null;
    }

    if (this.useLegacyWebKit) {
      try {
        video.webkitSetMediaKeys?.(null);
      } catch (_error) {
        // Detaching legacy keys can fail while a session is active.
      }
    } else if (video.mediaKeys) {
      try {
        await video.setMediaKeys(null);
      } catch (_error) {
        // Detaching MediaKeys can fail mid-load; the reload below recovers.
      }
      this.mediaKeysPromise = null;
      this.mediaKeysReady = false;
    }
  }

  /**
   * Reload the media element so WebKit re-parses the playlist and refires
   * key events, then best-effort restore position and playback.
   * @param {HTMLVideoElement} video
   */
  _restartPlayback(video) {
    const wasPlaying = video.paused === false && video.ended !== true;
    const resumeAt = Number.isFinite(video.currentTime) && video.currentTime > 0
      ? video.currentTime
      : null;

    try {
      video.load();
    } catch (error) {
      this.logger_.warn('Failed to reload media element:', error);
    }

    if (resumeAt !== null) {
      video.addEventListener('loadedmetadata', () => {
        try {
          video.currentTime = resumeAt;
        } catch (_error) {
          // Seeking can fail if metadata changed; playback restarts from 0.
        }
      }, { once: true });
    }

    if (wasPlaying) {
      Promise.resolve(video.play?.()).catch(() => {});
    }
  }

  /**
   * Close all tracked sessions, tolerating both modern (promise-returning)
   * and legacy (void) close() implementations.
   */
  _closeAllSessions() {
    for (const [keyId, session] of this.sessions) {
      this.logger_.debug(`Closing session for key ${keyId}`);
      try {
        const closeResult = session.close?.();
        closeResult?.catch?.(() => {});
      } catch (_error) {
        // Legacy WebKit sessions can throw synchronously on close.
      }
    }
    this.sessions.clear();
  }

  /**
   * Handle key status changes
   * @param {MediaKeySession} session
   * @param {string} keyId
   */
  _onKeyStatusChange(session, keyId) {
    session.keyStatuses.forEach((status, _key) => {
      this.logger_.debug(`Key ${keyId} status: ${status}`);

      switch (status) {
        case 'usable':
          this.logger_.info(`Key ${keyId} is now usable`);
          this.usableKeys.add(keyId);
          this._clearProcessingFailureTimeout();
          if (this._keyUsableTimeout) {
            clearTimeout(this._keyUsableTimeout);
            this._keyUsableTimeout = null;
          }
          break;

        case 'expired':
          this.logger_.warn(`Key ${keyId} expired`);
          this._dispatchError(
            'keySystemExpired',
            'The content license has expired.',
          );
          this.sessions.delete(keyId);
          session.close().catch(() => {});
          break;

        case 'internal-error':
          this.logger_.error(`Key ${keyId} internal error`);
          this._dispatchError(
            'keySystemStatusInternalError',
            'There was an error with the content protection system.',
          );
          this.sessions.delete(keyId);
          session.close().catch(() => {});
          break;

        case 'output-restricted':
          this.logger_.warn(`Key ${keyId} output restricted`);
          this._dispatchError(
            'keySystemStatusOutputRestricted',
            "This video cannot be played on your device because it doesn't meet the content protection requirements.",
          );
          break;

        case 'output-downscaled':
          this.logger_.warn(`Key ${keyId} output downscaled`);
          break;
      }
    });
  }

  /**
   * Dispatch an error to the player
   * @param {string} code - Error code
   * @param {string} message - Human-readable error message
   */
  _dispatchError(code, message) {
    if (this.onError) {
      this.onError(code, message);
    }
  }

  /**
   * Dispatch a DRM processing failure to the player.
   * @param {unknown} error
   */
  _dispatchErrorForFailure(error) {
    if (error?.drmFatal) {
      this._dispatchError(
        error?.drmErrorCode || 'keySystemError',
        error?.drmErrorMessage || DRM_ERROR_MESSAGE,
      );
      return;
    }

    if (this.usableKeys.size > 0) {
      return;
    }

    this._pendingProcessingFailure = error;
    this._startProcessingFailureTimeout();
  }

  /**
   * Clear any delayed processing failure once playback has a usable key.
   */
  _clearProcessingFailureTimeout() {
    this._pendingProcessingFailure = null;
    if (this._processingFailureTimeout) {
      clearTimeout(this._processingFailureTimeout);
      this._processingFailureTimeout = null;
    }
    this._cancelQueuedWebkitNeedKeyEvents();
  }

  /**
   * Delay per-key failures so duplicate/extra key events do not mask playback
   * when another key succeeds shortly afterwards.
   */
  _startProcessingFailureTimeout() {
    if (this.usableKeys.size > 0 || this._processingFailureTimeout) {
      return;
    }

    this._processingFailureTimeout = setTimeout(() => {
      this._processingFailureTimeout = null;

      if (this.usableKeys.size > 0) {
        this._pendingProcessingFailure = null;
        return;
      }

      const error = this._pendingProcessingFailure;
      this._pendingProcessingFailure = null;
      this._dispatchError(
        error?.drmErrorCode || 'keySystemError',
        error?.drmErrorMessage || DRM_ERROR_MESSAGE,
      );
    }, 10000);
  }

  /**
   * Start a timeout to detect if keys never become usable
   * This can happen due to HDCP/output restrictions that aren't explicitly reported
   */
  _startKeyUsableTimeout() {
    if (this.usableKeys.size > 0) {
      return;
    }

    // Only start once
    if (this._keyUsableTimeout) {
      return;
    }

    // Wait 10 seconds for at least one key to become usable
    this._keyUsableTimeout = setTimeout(() => {
      if (this.usableKeys.size === 0 && this.sessions.size > 0) {
        this.logger_.error('DRM keys failed to become usable');
        this._dispatchError(
          'keySystemInitError',
          'There was an error during initialization of the DRM system.',
        );
      }
    }, 10000);
  }

  /**
   * Fetch license from license server
   * @param {MediaKeyMessageEvent} messageEvent
   * @param {string} url
   * @returns {Promise<ArrayBuffer>}
   */
  async getResponse(messageEvent, url) {
    this.logger_.debug('Fetching license from:', url);

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: messageEvent.message,
      });
    } catch (error) {
      throw createDrmError(
        'keySystemLicenseRequestFailed',
        DRM_ERROR_MESSAGE,
        error,
      );
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'unknown');
      throw createDrmError(
        'keySystemLicenseRequestFailed',
        DRM_ERROR_MESSAGE,
        new Error(`License fetch failed: ${response.status} - ${errorText}`),
      );
    }

    const ckc = await response.arrayBuffer();
    if (ckc.byteLength === 0) {
      throw createDrmError(
        'keySystemLicenseRequestFailed',
        DRM_ERROR_MESSAGE,
        new Error('License server returned empty response'),
      );
    }

    return ckc;
  }

  /**
   * Clean up all sessions and event listeners
   */
  cleanup() {
    this.logger_.debug('Cleaning up DRM engine');

    // Clear timeout
    if (this._keyUsableTimeout) {
      clearTimeout(this._keyUsableTimeout);
      this._keyUsableTimeout = null;
    }
    if (this._processingFailureTimeout) {
      clearTimeout(this._processingFailureTimeout);
      this._processingFailureTimeout = null;
    }
    this._cancelQueuedWebkitNeedKeyEvents();

    // Remove event listener
    if (this._videoElement && this._boundOnEncrypted) {
      this._videoElement.removeEventListener(
        'encrypted',
        this._boundOnEncrypted,
      );
    }
    if (this._videoElement && this._boundOnWebkitNeedKey) {
      this._videoElement.removeEventListener(
        'webkitneedkey',
        this._boundOnWebkitNeedKey,
      );
    }

    // Close all sessions
    this._closeAllSessions();

    // Reset state
    this.pendingRequests.clear();
    this.usableKeys.clear();
    this._pendingProcessingFailure = null;
    this.preparedMediaKeysPromise = null;
    this.mediaKeysPromise = null;
    this.mediaKeysReady = false;
    this._hasStandardEncryptedEvent = false;
    this.useLegacyWebKit = false;
    this._pathSwitchAttempted = false;
    this._videoElement = null;
    this._boundOnEncrypted = null;
    this._boundOnWebkitNeedKey = null;
  }

  /**
   * Get the number of active sessions
   * @returns {number}
   */
  get sessionCount() {
    return this.sessions.size;
  }
}
